import type { StyleElements } from "replo-runtime/shared/utils/styles";
import type { CustomPropsRecord } from "schemas/component";
import type { ComponentConfig } from "../../components";

import { mapAndConvertComponentStylePropsToStyle } from "replo-runtime/shared/utils/breakpoints";

import { convertToLegacyProps } from "../../../shared/utils/props";

function getConfigurableProps(): CustomPropsRecord {
  return {
    _seconds: {
      name: "Seconds per rotation",
      type: "seconds",
      description:
        "Number of seconds it takes the marquee to slide past one repetition of its components",
      defaultValue: "10s",
    },
    _rightDirection: {
      name: "Scroll to the right",
      type: "boolean",
      description: "If enabled, the ticker will scroll to the right.",
      defaultValue: false,
    },
    /**
     * Note (Noah, 2023-06-14, USE-203): When the marquee component was first
     * designed we had it duplicate its children 20x so that it could scroll
     * infinitely and look "correct" even with a small number of child
     * components. However, this causes massive performance problems. For most
     * marquee components, the correct answer to this should be to just
     * duplicate the child components enough times that they cover the whole
     * screen, but updating this might break existing marquees, so instead of
     * having a MarqueeV2, we have this internal prop which allows opting into
     * the new, more performant behavior when you add a new template.
     *
     * As long as all our templates have this internal prop set, all new marquee
     * components shouldn't have this perf issue.
     */
    _internalUseRepetition: {
      name: "[Admin Only] Use default repetition",
      type: "boolean",
      description:
        "Internal use only. If you're seeing this, contact support@replo.app.",
      defaultValue: true,
      shouldDisplay: ({ userIsSuperuser }) => userIsSuperuser,
    },
  };
}

const customProperties = {
  marqueeRepetitions: "--replo-marquee-repetitions",
};

const styleElements = {
  root: {
    defaultStyles({ component }) {
      const useRepetition = component.props._internalUseRepetition ?? true;
      const numberOfRepetitions = useRepetition ? 20 : 2;
      return {
        overflowX: "hidden",
        [customProperties.marqueeRepetitions]: String(numberOfRepetitions),
      };
    },
    overrideStyles({ styleProps }) {
      // NOTE (Martin 2024-12-23): We need to make sure that the ticker root
      // component is always rendered in row direction. We had a USE in the past
      // (USE-318) where we had a ticker that was rendered in column direction
      // and it was broken. We implemented a fix for it, but it was a hack and
      // it introduced hydration issues (USE-1518).
      return mapAndConvertComponentStylePropsToStyle(styleProps, () => {
        return {
          flexDirection: "row",
        };
      });
    },
    childrenOverrideStyles: {
      flexShrink: 0,
      whiteSpace: "normal",
      // Note (Noah, 2023-11-09, USE-402): For some reason that only god and
      // Tim Cook know, Safari has issues with translating elements which have
      // images inside them. Setting this transform causes Safari to not
      // flicker when resetting the transform animation that we use to animate
      // the marquee. See e.g.
      // https://stackoverflow.com/questions/25010353/safari-css-transition-flickering,
      // https://developer.apple.com/forums/thread/705172,
      // https://github.com/nolimits4web/swiper/issues/3527, etc
      WebkitTransform: "translateZ(0)",
    },
  },
  inner: {
    defaultStyles({ component }) {
      const seconds = component.props._seconds;
      const rightDirection = component.props._rightDirection;
      return {
        display: "flex",
        flexDirection: "row",
        whiteSpace: "nowrap",
        willChange: "transform",
        overflow: "visible",
        flexShrink: 0,
        width: "auto",
        animationName: "replo-marquee",
        animationDuration: seconds,
        animationTimingFunction: "linear",
        animationIterationCount: "infinite",
        animationDirection: rightDirection ? "reverse" : undefined,
      };
    },
  },
} satisfies StyleElements;

const config = {
  renderData: {
    newInstancesUseNumbering: true,
    customProps: convertToLegacyProps(getConfigurableProps()),
    acceptsArbitraryChildren: () => false,
    isAlwaysDynamic: false,
    canvasIndicatorDragDirections: [],
    allowsLayoutModification: false,
    canContainChildren: true,
    showComponentControlsFromChildren: true,
    styleElements,
    ancestorDisallowList: [
      {
        ancestorTypes: ["marquee"],
        message: "Tickers cannot be nested inside tickers.",
      },
    ],
  },
} satisfies ComponentConfig;

export default config;
